import { useCallback, useEffect, useRef, useState } from 'react';
import { useDebounce } from 'hooks';

import {
  DEFAULT_SPLIT_PERCENT,
  MAX_LEFT_WIDTH_IN_PX,
  MILLISECONDS_TO_WAIT,
  MIN_LEFT_WIDTH_IN_PX
} from './DualPanelLayout.constants';

export const useDualPanelLayout = ({
  defaultSplitPercent = DEFAULT_SPLIT_PERCENT,
  maxLeftWidthInPx = MAX_LEFT_WIDTH_IN_PX,
  minLeftWidthInPx = MIN_LEFT_WIDTH_IN_PX,
  storageKey
}) => {
  const containerRef = useRef(null);
  const dragging = useRef(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [splitPercentage, setSplitPercentage] = useState(() => {
    const saved = localStorage.getItem(storageKey);
    return saved ? Number(saved) : defaultSplitPercent;
  });

  const debouncedSplitPercentage = useDebounce(splitPercentage.toString(), MILLISECONDS_TO_WAIT);

  useEffect(() => {
    if (storageKey && debouncedSplitPercentage) {
      localStorage.setItem(storageKey, debouncedSplitPercentage);
    }
  }, [debouncedSplitPercentage, storageKey]);

  const leftWidthPercentage = isExpanded ? splitPercentage * 100 : 0;
  const rightWidthPercentage = 100 - leftWidthPercentage;

  const toggleExpanded = useCallback(() => setIsExpanded(prev => !prev), []);

  const handleMouseMove = useCallback(
    e => {
      if (!dragging.current || !containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const newLeftWidth = e.clientX - rect.left;

      const clampedPx = Math.min(Math.max(newLeftWidth, minLeftWidthInPx), maxLeftWidthInPx);
      const clampedRatio = clampedPx / rect.width;
      setSplitPercentage(clampedRatio);
    },
    [maxLeftWidthInPx, minLeftWidthInPx]
  );

  const stopDragging = useCallback(() => {
    dragging.current = false;
    document.body.style.userSelect = '';
    window.removeEventListener('mousemove', handleMouseMove);
    window.removeEventListener('mouseup', stopDragging);
  }, [handleMouseMove]);

  const startDragging = useCallback(
    e => {
      dragging.current = true;
      document.body.style.userSelect = 'none';
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', stopDragging);
      e.preventDefault();
    },
    [handleMouseMove, stopDragging]
  );

  useEffect(() => stopDragging, [stopDragging]);

  const containerWidth = containerRef.current?.getBoundingClientRect().width || 1000;
  const minSplitPercent = minLeftWidthInPx / containerWidth;
  const maxSplitPercent = maxLeftWidthInPx / containerWidth;

  return {
    containerRef,
    isExpanded,
    leftWidthPercentage: `${leftWidthPercentage}%`,
    rightWidthPercentage: `${rightWidthPercentage}%`,
    maxSplitPercent,
    minSplitPercent,
    splitPercentage,
    startDragging,
    stopDragging,
    toggleExpanded
  };
};
